import {
  ICheckoutCartOffer,
  ICheckoutCartOfferAuthor,
  ICheckoutCartOfferTestimonial,
  ICheckoutCartOfferAvailability,
  ICheckoutCartOfferEligibility,
  ICheckoutCartOfferPayment,
  ICheckoutCartOfferCoupon,
  ICheckoutCartOfferRelated,
  ICheckoutCart,
  ICheckoutCartOfferDiscount,
  ICheckoutCartOfferPrice
} from '@/interfaces/Checkout';

import i18n from '@/plugins/i18n';

import { EIntervalTypes, EEligibilityAction } from '@/helpers/enums';
import { Invoice } from './Invoice';
import { Payment } from './Payment';
import { IRewardsSummary } from '@/interfaces/Rewards';

export class OfferPrice implements ICheckoutCartOfferPrice {
  id: number;
  price: string | number;
  points_price: string | number;
  isSelected?: boolean = false;
  isAvailable?: boolean = false;

  constructor(offerPrice: ICheckoutCartOfferPrice | null) {
    Object.assign(this, { ...offerPrice });
  }
}

export class Offer implements ICheckoutCartOffer {
  id?: number;
  name?: string;
  display_name?: string;
  subtitle?: string | null;
  price?: number;
  offer_price?: ICheckoutCartOfferPrice[] = new Array<ICheckoutCartOfferPrice>();
  slug?: string;
  authors?: ICheckoutCartOfferAuthor[] = new Array<ICheckoutCartOfferAuthor>();
  authors_names?: string;
  offer_testimonials?: ICheckoutCartOfferTestimonial[] = new Array<ICheckoutCartOfferTestimonial>();
  availability?: ICheckoutCartOfferAvailability;
  eligibility?: ICheckoutCartOfferEligibility | null;
  payment_structure?: ICheckoutCartOfferPayment[];
  coupon?: ICheckoutCartOfferCoupon | null;
  related_offers?: ICheckoutCartOffer[] | ICheckoutCartOfferRelated[] | null = new Array<ICheckoutCartOfferRelated>();
  interval_type?: EIntervalTypes;
  interval_count?: number;
  target_content_link_text?: string | null;
  target_content_link_url?: string | null;
  html_description?: string | null;
  short_description?: string | null;
  has_trial?: boolean;
  trial_days?: number | null;
  trial_price?: number | null;
  address_text_title?: string;
  is_address_required?: boolean;
  error_code?: number | string | null;
  is_selected?: boolean = false;
  related_id?: number;
  discounts?: {
    error_code: number | null;
    discounts: ICheckoutCartOfferDiscount[];
  };
  related_offer_title?: string;

  payment: Payment = new Payment();
  invoice: Invoice = new Invoice();

  cart_info?: ICheckoutCart;
  price_info?: { priceId: number, rewards: IRewardsSummary };

  constructor(offer: ICheckoutCartOffer | null, cart?: ICheckoutCart, price?: { priceId: number, rewards: IRewardsSummary }) {
    Object.assign(this, { ...offer });

    this.cart_info = cart;
    this.price_info = price;

    if (offer) {
      this.authors_names = Offer.generateAuthorsName(offer);

      if (Offer.isAvailable(offer) && Offer.isEligible(offer) && offer.related_offers) {
        const relatedOffers = offer.related_offers as ICheckoutCartOfferRelated[];

        this.related_offers = relatedOffers && relatedOffers.filter((offer: ICheckoutCartOfferRelated) => {
          return Offer.isAvailable(offer.related_offer_data || offer) && Offer.isEligible(offer.related_offer_data || offer);
        }).map((relatedOffer: ICheckoutCartOfferRelated) => {
          const offerData = {
            ...relatedOffer.related_offer_data || relatedOffer,
            ...{
              order: relatedOffer.order,
              is_best_choice: relatedOffer.is_best_choice,
            },
            ...relatedOffer.title && {
              related_offer_title: relatedOffer.title,
            }
          };

          return !cart
            ? new Offer(offerData)
            : new Offer(offerData, cart, price);
        });
      }

      const hasRelatedSelected = this.related_offers && this.related_offers.some((offer: any) => offer.is_selected);

      if (cart) {
        this.offer_price = this.offer_price && Offer.sortOfferPrice(this.offer_price);
        this.offer_price = this.offer_price && this.offer_price.map((offerPrice: ICheckoutCartOfferPrice) => new OfferPrice({
          ...offerPrice,
          price: Number(offerPrice.price),
          ...price && price.rewards && {
            isValid: Offer.offerPriceIsValidToBuy(offer, offerPrice.id),
            isAvailable: Offer.canBuyOfferPriceWithPoints(this, {
              priceId: offerPrice.id,
              rewards: price.rewards,
            }),
          },
        }));

        const paymentStructure = Offer.paymentStructureInitial(this);

        if (paymentStructure) {
          this.selectOfferPrice(paymentStructure.price_id);
        }

        if (paymentStructure) {
          this.payment = new Payment(cart, cart.customer, paymentStructure);
          this.invoice = new Invoice(paymentStructure, this.payment);
        }

        if (cart.offer.id === offer.id && !hasRelatedSelected) {
          this.is_selected = true;
        }
      }
    }
  }

  public selectOfferPrice(priceId: number, ignoreAvailability: boolean = false): number | void {
    if (!ignoreAvailability && !Offer.canBuyOfferPrice(this, priceId)) return;

    if (this.offer_price && this.offer_price.length) {
      this.offer_price = this.offer_price.map((offerPrice: ICheckoutCartOfferPrice) => ({
        ...offerPrice,
        isSelected: offerPrice.id === priceId,
      }));
    } else {
      this.offer_price = [];
    }

    const paymentStructure = Offer.paymentStructureByPriceId(this, priceId);

    this.payment = this.payment && this.payment.updatePaymentStructure(paymentStructure);
    this.invoice = new Invoice(paymentStructure, this.payment);

    return priceId;
  }

  public selectOfferInitialPrice(ignoreAvailability: boolean = false): void | number {
    const initialPaymentStructure = Offer.paymentStructureInitial(this, ignoreAvailability);

    if (initialPaymentStructure) {
      return this.selectOfferPrice(initialPaymentStructure.price_id, ignoreAvailability);
    }
  }

  public static canBuySomeOfferPriceUsingRewardsPointsToo(offer: ICheckoutCartOffer, rewards: IRewardsSummary): boolean | undefined {
    const validToBuyOfferPrices: ICheckoutCartOfferPrice[] | undefined = Offer.offerPricesValidToBuy(offer);
    const offerPricesWithoutPoints: ICheckoutCartOfferPrice[] | undefined = Offer.offerPricesWithoutPoints(offer);

    if (offerPricesWithoutPoints && offerPricesWithoutPoints.length) {
      return true;
    }

    return validToBuyOfferPrices && validToBuyOfferPrices.some((offerPrice: ICheckoutCartOfferPrice) => {
      return offerPrice.points_price <= rewards.point;
    });
  }

  public static offerPaymentStructureIsValidToBuy(paymentStructure: ICheckoutCartOfferPayment): boolean | undefined {
    return Boolean(paymentStructure.payment_conditions && paymentStructure.payment_conditions.length);
  }

  public static offerIsValidToBuy(offer: ICheckoutCartOffer): boolean | undefined {
    const buyableOfferPrices = Offer.offerPricesValidToBuy(offer);

    return Boolean(buyableOfferPrices && buyableOfferPrices.length);
  }

  public static offerPriceIsValidToBuy(offer: ICheckoutCartOffer, offerPriceId: ICheckoutCartOfferPrice['id']): boolean | undefined {
    const paymentStructure = Offer.paymentStructureByPriceId(offer, offerPriceId);

    if (!paymentStructure) return false;

    return Offer.offerPaymentStructureIsValidToBuy(paymentStructure);
  }

  public static offerPricesWithoutPoints(offer: ICheckoutCartOffer): ICheckoutCartOfferPrice[] | undefined {
    const validOfferPrices = Offer.offerPricesValidToBuy(offer);

    return validOfferPrices && validOfferPrices
      .filter((offerPrice: ICheckoutCartOfferPrice) => !offerPrice.points_price);
  }

  public static offerPricesValidToBuy(offer: ICheckoutCartOffer): ICheckoutCartOfferPrice[] | undefined {
    return offer.offer_price && offer.offer_price
      .filter((offerPrice: ICheckoutCartOfferPrice) => Offer.offerPriceIsValidToBuy(offer, offerPrice.id));
  }

  public static canBuySomeOfferPrice(offer: ICheckoutCartOffer): boolean | undefined {
    return offer.offer_price && offer.offer_price.some((offerPrice: ICheckoutCartOfferPrice) => {
      return offerPrice.isAvailable;
    });
  }

  public static canBuyOfferPrice(offer: ICheckoutCartOffer, priceId: number): boolean {
    const offerPrice = Offer.offerOfferPriceById(offer, priceId);
    const offerIsValidToBuy = Offer.offerPriceIsValidToBuy(offer, priceId);

    return Boolean(offerPrice && offerPrice.isAvailable && offerIsValidToBuy);
  }

  public static canBuyOfferPriceWithPoints(offer: ICheckoutCartOffer, price?: { priceId: number, rewards: IRewardsSummary }): boolean {
    if (!price) return true;

    const offerPrice = Offer.offerOfferPriceById(offer, price.priceId);

    if (!offerPrice || !offerPrice.points_price) return true;

    return Number(offerPrice.points_price) <= Number(price.rewards.point);
  }

  public static offerOfferPriceById(offer: ICheckoutCartOffer, priceId?: number): ICheckoutCartOfferPrice | undefined {
    return offer.offer_price && offer.offer_price.find((offerPrice: ICheckoutCartOfferPrice) => {
      return offerPrice.id === priceId;
    });
  }

  public static paymentStructureByPriceId(offer: ICheckoutCartOffer, priceId: number): ICheckoutCartOfferPayment | void {
    if (!offer.payment_structure) return;

    return offer.payment_structure.find((paymentStructure: ICheckoutCartOfferPayment) => {
      return paymentStructure.price_id === priceId;
    });
  }

  public static paymentStructureInitial(offer: ICheckoutCartOffer, ignoreAvailability: boolean = false): ICheckoutCartOfferPayment | any {
    const validToBuyOfferPrices: ICheckoutCartOfferPrice[] | undefined = Offer.offerPricesValidToBuy(offer);
    const availableOfferPrices: ICheckoutCartOfferPrice[] | undefined = validToBuyOfferPrices && validToBuyOfferPrices
      .filter((offerPrice: ICheckoutCartOfferPrice) => offerPrice.isAvailable);


    if (!ignoreAvailability && (availableOfferPrices && !availableOfferPrices.length || !availableOfferPrices)) return;

    const offerPrices = ignoreAvailability
      ? validToBuyOfferPrices
      : availableOfferPrices;

    const greaterOfferPrice: ICheckoutCartOfferPrice | undefined = offerPrices && offerPrices
      .reduce((prev: ICheckoutCartOfferPrice, current: ICheckoutCartOfferPrice) => {
        return (prev.points_price > current.points_price) ? prev : current;
      });

    if (greaterOfferPrice && offer.payment_structure) {
      const paymentStructureInitial = this.generatingPaymentStructure(offer, greaterOfferPrice.id);

      return paymentStructureInitial;
    }
  }

  public static generatingPaymentStructure(offer: ICheckoutCartOffer, offerPriceId: number): any {
    let paymentStructureArr: any = {};
    let paymentConditions: any = [];
    let additionalPaymentConditions: any = [];

    if (offer && offer.payment_structure) {
      offer.payment_structure.forEach((payment_structure_value: any) => {
        if (!payment_structure_value.is_multi_payment_options && payment_structure_value.payment_conditions.length === 1) {
          paymentConditions.push({
            offer_payment_condition_id: payment_structure_value.offer_payment_condition_id,
            ...payment_structure_value.payment_conditions[0],
          });
        }

        if (payment_structure_value.is_multi_payment_options)
          additionalPaymentConditions.push({
            offer_payment_condition_id: payment_structure_value.offer_payment_condition_id,
            ...this.generateAdditionalPaymentConditions(payment_structure_value)
          });
      });

      const paymentStructureDefault = offer.payment_structure.find((paymentStructure: ICheckoutCartOfferPayment) => paymentStructure.price_id === offerPriceId);

      paymentStructureArr = {
        ...paymentStructureDefault,
        payment_conditions: paymentConditions,
        additional_payment_conditions: additionalPaymentConditions
      };
    }

    return paymentStructureArr;
  }

  public static generateAdditionalPaymentConditions(payment_structure: any): any {
    let additionalPaymentCondition: any = {};
    let paymentMethodCode = payment_structure.payment_conditions.reduce((a: any, b: any) => {
      return a.payment_method_code + '_' + b.payment_method_code;
    });

    if (payment_structure) {
      additionalPaymentCondition = {
        is_multi_payment_options: payment_structure.is_multi_payment_options,
        payment_method_code: paymentMethodCode,
        payment_conditions: payment_structure.payment_conditions,
      };
    }

    return additionalPaymentCondition;
  }

  public static sortOfferPrice(offerPrice?: ICheckoutCartOfferPrice[]): ICheckoutCartOfferPrice[] | undefined {
    if (!offerPrice) return offerPrice;

    return offerPrice
      .sort((a, b) => Number(a.points_price) < Number(b.points_price) ? 1 : -1);
  }

  public static isAvailable(offer: ICheckoutCartOffer): boolean {
    return Boolean(offer && offer.availability && offer.availability.available);
  }

  public static isEligible(offer: ICheckoutCartOffer): boolean {
    const eligibility = offer.eligibility;
    const eligibleActions = [EEligibilityAction.purchase, EEligibilityAction.purchaseRebuy, EEligibilityAction.purchaseUpgrade];

    return Boolean(eligibility && eligibility.eligible && eligibleActions.includes(eligibility!.eligible_action));
  }

  public static related(offer: ICheckoutCartOffer): ICheckoutCartOffer[] {
    let relatedOffers = [offer, ...offer.related_offers as ICheckoutCartOffer[]];

    if (relatedOffers) {
      relatedOffers = relatedOffers
        .sort((a, b) => Number(a.order) > Number(b.order) ? 1 : -1)
        .sort((a, b) => a.id === offer.id ? 1 : -1)
        .sort((a, b) => a.is_best_choice ? 1 : -1);
    }

    return relatedOffers;
  }

  public static generateAuthorsName(offer: ICheckoutCartOffer): string {
    if (!offer) return '';

    const uniqueAuthors = offer.authors && offer!.authors.filter((author, index, array) => {
      return array.findIndex(a => a.name === author.name) === index;
    });

    return uniqueAuthors
      ? uniqueAuthors.reduce((acc, act, index, arr) => {
        const isFirst: boolean = acc.length === 0;
        const isLast: boolean = arr.length - 1 === index;
        if (isFirst) return act.name;
        if (isLast) return `${acc} ${i18n.t('App.Checkout.Offer.and')} ${act.name}`;
        return `${acc}, ${act.name}`;
      }, '')
      : '';
  }
}
